import { hasUserPermission } from '../user'
import { entries } from '../object'
import { isDefined, isEmpty, isNotDefined, isNotEmpty } from '../isEmpty'
import { isDeleted, isNotDeleted } from '../common'
import { isValidHexColor } from '../color'
import { isTrue } from '../boolean'
import { unique } from '../array'
import {
  COMPANY_ICON_ID,
  isWorkflowActionId,
  WORKFLOW_PROSPECT_STAGE_ID,
  type AccountData,
  type CheckpointBehavior,
  type CloserEventFlow,
  type CloserInteraction,
  type CloserModel,
  type IOType,
  type ReportColumnDisplay,
  type RequiredAccountField,
  type TagId,
  type UndoActionPreferences,
  type UserId,
  type UserProfileData,
  type WorkflowActionData,
  type WorkflowActionId,
  type WorkflowStageAiConfig,
  type WorkflowCondition,
  type WorkflowData,
  type WorkflowEntityType,
  type WorkflowEventConfig,
  type WorkflowId,
  type WorkflowNoteConfig,
  type WorkflowReportConfig,
  type WorkflowScheduleRestriction,
  type WorkflowStageData,
  type WorkflowStageEventConfig,
  type WorkflowStageId,
} from '../../model'
import { isWorkflowPipelineStage } from './stage'
import { getActionTileLabel } from './icon'
import { DEFAULT_NOTE_TIMEFRAME_MS, DEFAULT_UNDO_ACTION_TIME_SINCE, PROSPECT_STAGE_COLOR } from './constants'
import { getWorkflowAccountLastActivity } from './account'

export class Workflow {
  private readonly _data: WorkflowData
  private readonly _allStages: WorkflowStage[]
  private readonly _stagesMap: Map<WorkflowStageId, WorkflowStage>
  private readonly _allActions: WorkflowAction[]
  private readonly _actionsMap: Map<WorkflowActionId, WorkflowAction>

  constructor(data: WorkflowData) {
    this._data = data
    this._allStages = data.stages.map((stage) => new WorkflowStage(stage, this))
    this._stagesMap = new Map(this._allStages.map((stage) => [stage.id, stage]))
    this._allActions = data.actions.map((action) => new WorkflowAction(action, this))
    this._actionsMap = new Map(this._allActions.map((action) => [action.id, action]))
  }

  get data(): WorkflowData {
    return this._data
  }

  get id(): WorkflowId {
    return this._data.workflowId
  }

  get stages(): WorkflowStage[] {
    return this._allStages
  }

  get pipelineStages(): WorkflowStage[] {
    return this._allStages
      .filter((stage) => stage.isPipelineStage && isNotDeleted(stage))
      .sort((a, b) => {
        if (a.order === b.order) return a.name.localeCompare(b.name)
        return a.order - b.order
      })
  }

  get nonPipelineStages(): WorkflowStage[] {
    return this._allStages
      .filter((stage) => !stage.isPipelineStage && isNotDeleted(stage))
      .sort((a, b) => {
        if (a.name === b.name) return a.id.localeCompare(b.id)
        return a.name.localeCompare(b.name)
      })
  }

  get actions(): WorkflowAction[] {
    return this._allActions
  }

  get orderedActions(): WorkflowAction[] {
    return sortWorkflowActions(this._allActions)
  }

  get entity(): WorkflowEntityType {
    return this._data.entity ?? 'account'
  }

  get isAccountWorkflow(): boolean {
    return this.entity === 'account'
  }

  get isUserWorkflow(): boolean {
    return this.entity === 'user'
  }

  get archivedStages(): WorkflowStage[] {
    return this._allStages.filter(isDeleted)
  }

  get archivedActions(): WorkflowAction[] {
    return this._allActions.filter(isDeleted)
  }

  public getStage(stageId: WorkflowStageId | undefined): WorkflowStage | undefined {
    return this._stagesMap.get(stageId ?? WORKFLOW_PROSPECT_STAGE_ID)
  }

  public getAction(actionId: WorkflowActionId | undefined): WorkflowAction | undefined {
    if (isNotDefined(actionId)) return
    return this._actionsMap.get(actionId)
  }

  public getAvailableActions(stageId: WorkflowStageId | undefined, userPerm: UserPerm | undefined): WorkflowAction[] {
    const stage = this.getStage(stageId)
    if (isNotDefined(stage)) return []
    return stage.getAvailableActions(userPerm)
  }

  public getStageName(stageId: WorkflowStageId | undefined): string {
    const stage = this.getStage(stageId)
    return stage?.name ?? '[Deleted Stage]'
  }

  public getStageShortName(stageId: WorkflowStageId | undefined): string {
    const stage = this.getStage(stageId)
    return stage?.short ?? this.getStageName(stageId)
  }

  public getActionName(actionId: WorkflowActionId | undefined): string {
    const action = this.getAction(actionId)
    return action?.name ?? '[Deleted Action]'
  }

  public getActionShortName(actionId: WorkflowActionId | undefined): string {
    const action = this.getAction(actionId)
    return action?.short ?? this.getActionName(actionId)
  }

  public getActivityDataDisplayProps({
    stageId,
    actionId,
  }: {
    stageId: WorkflowStageId | undefined
    actionId?: WorkflowActionId | TagId
  }): PinDisplayProps {
    const currentStage = this.getStage(stageId)

    if (isNotDefined(currentStage)) return { type: 'initial' }
    if (isDefined(actionId)) {
      if (isWorkflowActionId(actionId)) {
        if (currentStage.useCompanyLogo) return { type: 'logo', color: currentStage.color }
        const action = this.getAction(actionId)
        if (isDefined(action)) return { type: 'icon', color: action.color ?? currentStage.color, icon: action.icon }
      } else {
        return { type: 'tag', tagId: actionId }
      }
    }
    return currentStage.displayProps
  }

  public getAccountDisplayProps(
    account: Pick<AccountData, 'workflowHistory' | 'tags'> | undefined,
    shouldIncludeTags?: boolean
  ): PinDisplayProps {
    if (isNotDefined(account)) return { type: 'initial' }
    const lastActivity = getWorkflowAccountLastActivity(account, shouldIncludeTags)
    if (isNotDefined(lastActivity)) return { type: 'initial' }
    return this.getActivityDataDisplayProps({
      stageId: lastActivity.stageId,
      actionId: lastActivity.actionId,
    })
  }

  public getStageRequiredFields(stageId: WorkflowStageId | undefined): RequiredAccountField[] | undefined {
    if (isNotDefined(stageId)) return
    const stage = this._stagesMap.get(stageId)
    if (isNotDefined(stage)) return
    if (stage.isDeleted) return
    if (!stage.isPipelineStage) return stage.requiredFields
    const stageIdx = this.pipelineStages.findIndex((s) => s.id === stageId)
    if (stageIdx === -1) return stage.requiredFields
    const stages = this.pipelineStages.slice(0, stageIdx + 1)
    return unique(stages.flatMap((s) => s.requiredFields))
  }

  public getIncomingActions(stageId: WorkflowStageId | undefined): WorkflowAction[] {
    const stage = this.getStage(stageId)
    if (isNotDefined(stage)) return []
    return this._allActions.filter((a) => {
      if (isDeleted(a)) return false
      if (isDefined(a.toStage)) return a.toStage.id === stageId
      if (isNotEmpty(a.checkpoints)) {
        const lastAlwaysCheckpoint = a.checkpoints.findLast((c) => c.behavior === 'always')
        if (isDefined(lastAlwaysCheckpoint) && lastAlwaysCheckpoint.stage.id === stageId) return true
      }
      return false
    })
  }

  private getLastPipelineStage(
    account: Pick<AccountData, 'workflowStageId' | 'workflowHistory'> | undefined
  ): WorkflowStage | undefined {
    const currentStage = this.getStage(account?.workflowStageId)
    if (isNotDefined(currentStage)) return
    if (currentStage.isPipelineStage) return currentStage

    for (const item of account?.workflowHistory ?? []) {
      const stage = this.getStage(item.stageId)
      if (isNotDefined(stage)) continue
      if (stage.isPipelineStage) return stage
    }
    return this.getStage(WORKFLOW_PROSPECT_STAGE_ID)
  }

  public getActionCheckpoints(
    account: Pick<AccountData, 'workflowStageId' | 'workflowHistory'> | undefined,
    actionId: WorkflowActionId
  ): ActionStageCheckpoint[] | undefined {
    const action = this.getAction(actionId)
    if (isNotDefined(action)) return
    if (isEmpty(action.checkpoints)) return

    const lastPipelineStage = this.getLastPipelineStage(account)
    if (isNotDefined(lastPipelineStage)) return

    const validCheckpoints = action.checkpoints.filter((c) => c.stage.order > lastPipelineStage.order)
    if (isEmpty(validCheckpoints)) return
    const nextNeverCheckpoint = validCheckpoints.findIndex((c) => c.behavior === 'never')
    if (nextNeverCheckpoint === -1) return validCheckpoints
    return validCheckpoints.slice(0, nextNeverCheckpoint)
  }

  public sortAccountsByStageAndTag(
    a: AccountData,
    b: AccountData,
    args: {
      userId: UserId
    }
  ): number {
    const aStage = a.workflowStageId ? this.getStage(a.workflowStageId) : undefined
    const bStage = b.workflowStageId ? this.getStage(b.workflowStageId) : undefined

    const aIsPipeline = aStage?.isPipelineStage
    const bIsPipeline = bStage?.isPipelineStage
    const aIsStartStage = aStage?.isInitial
    const bIsStartStage = bStage?.isInitial

    if (!aIsStartStage && bIsStartStage) return -1
    if (aIsStartStage && !bIsStartStage) return 1

    if (aIsPipeline && !bIsPipeline) return -1
    if (!aIsPipeline && bIsPipeline) return 1

    if (aIsPipeline && bIsPipeline) {
      const orderDiff = bStage.order - aStage.order
      if (orderDiff !== 0) return orderDiff
    }

    if (!aIsPipeline && !bIsPipeline) {
      const aIsUser = a.ownerId === args.userId
      const bIsUser = b.ownerId === args.userId
      if (aIsUser && !bIsUser) return -1
      if (!aIsUser && bIsUser) return 1
    }

    const aLastActivity = getWorkflowAccountLastActivity(a, true)
    const bLastActivity = getWorkflowAccountLastActivity(b, true)
    if (isDefined(aLastActivity) && isNotDefined(bLastActivity)) return -1
    if (isNotDefined(aLastActivity) && isDefined(bLastActivity)) return 1
    if (isDefined(aLastActivity) && isDefined(bLastActivity)) {
      const diff = bLastActivity.timestamp - aLastActivity.timestamp
      if (diff !== 0) return diff
    }
    return 0
  }

  get undoActionPreferences(): UndoActionPreferences {
    const config = this._data.preferences?.undoAction ?? {}
    return {
      distanceFromLocation: config.distanceFromLocation ?? 0,
      timeSince: config.timeSince ?? DEFAULT_UNDO_ACTION_TIME_SINCE,
    }
  }

  public getActionUndoPreferences(actionId: WorkflowActionId | undefined): UndoActionPreferences {
    if (isNotDefined(actionId)) return this.undoActionPreferences
    const action = this.getAction(actionId)
    const { distanceFromLocation, timeSince } = this.undoActionPreferences
    const { undoAction } = action?.data.accountConfig ?? {}
    return {
      distanceFromLocation: undoAction?.distanceFromLocation ?? distanceFromLocation,
      timeSince: undoAction?.timeSince ?? timeSince,
    }
  }

  public canSeeHistory(user: Pick<UserProfileData, 'userId' | 'permissionMap' | 'roles'>): boolean {
    if (hasUserPermission(user, 'account:manage')) return true
    const limitedRoles = this.data.preferences?.hidePreviousActivity ?? []
    if (isEmpty(limitedRoles)) return true
    if (!user.roles?.some((role) => limitedRoles.includes(role.roleId))) return true
    return false
  }

  get deleteOnUndo(): boolean {
    return this._data.preferences?.deleteOnUndo === true
  }

  get closerEventFlow(): CloserEventFlow {
    return this._data.preferences?.closerEventFlow ?? { type: 'residentFirst' }
  }

  get mapControlKpis(): ReportColumnDisplay[] {
    const { mapControlKpis = [] } = this._data.preferences ?? {}
    return mapControlKpis
  }

  get showAllCustomFieldsInResidentForm(): boolean {
    return this._data.preferences?.showAllCustomFieldsInResidentForm === true
  }

  get defaultScheduleRestriction(): WorkflowScheduleRestriction | undefined {
    return this._data.preferences?.defaultEventConfig?.scheduleRestriction
  }
}

export class WorkflowStage {
  private readonly _data: WorkflowStageData
  private readonly _workflow: Workflow

  constructor(data: WorkflowStageData, workflow: Workflow) {
    this._data = data
    this._workflow = workflow
  }

  get data(): WorkflowStageData {
    return this._data
  }

  get id(): WorkflowStageId {
    return this._data.stageId
  }

  get name(): string {
    return this._data.name
  }

  get short(): string {
    return getActionTileLabel(this._data)
  }

  get plural(): string {
    const { name, plural } = this._data
    return plural || name
  }

  get description(): string | undefined {
    return this._data.description
  }

  get order(): number {
    return this._data.order ?? 0
  }

  get color(): string {
    if (this.isInitial || isEmpty(this._data.color)) return PROSPECT_STAGE_COLOR
    return this._data.color
  }

  get requiredFields(): RequiredAccountField[] {
    return this._data.requiredFields ?? []
  }

  get actionIds(): WorkflowActionId[] {
    return this._data.actionIds ?? []
  }

  get ioType(): IOType | undefined {
    return this._data.ioType
  }

  get actions(): WorkflowAction[] {
    return (
      this._data.actionIds
        ?.map((actionId) => this._workflow.getAction(actionId))
        .filter(isDefined)
        .filter(isNotDeleted)
        .filter((a) => {
          if (isDefined(a.toStage) && isDeleted(a.toStage)) return false
          return true
        }) ?? []
    )
  }

  public getAvailableActions(userPerm: UserPerm | undefined): WorkflowAction[] {
    if (isNotDefined(userPerm)) return this.actions
    return this.actions.filter((a) => a.hasPermission(userPerm))
  }

  get isInitial(): boolean {
    return this._data.stageId === WORKFLOW_PROSPECT_STAGE_ID
  }

  get useCompanyLogo(): boolean {
    return this._data.icon === COMPANY_ICON_ID
  }

  get displayProps(): PinDisplayProps {
    if (this.isInitial) return { type: 'initial' }
    if (this.useCompanyLogo) return { type: 'logo', color: this.color }
    return { type: 'icon', color: this.color }
  }

  get isPipelineStage(): boolean {
    return isWorkflowPipelineStage(this._data)
  }

  get isOtherStage(): boolean {
    return !this.isPipelineStage
  }

  get isDeleted(): boolean {
    return isDeleted(this._data)
  }

  get isFinalStage(): boolean {
    const finalStage = this._workflow.pipelineStages.at(-1)
    return this.id === finalStage?.id
  }

  get eventConfig(): WorkflowStageEventConfig | undefined {
    if (this.isInitial) return
    return this._data.eventConfig
  }

  get noteConfig(): Required<WorkflowNoteConfig> | undefined {
    if (this.isInitial) return
    return decorateNoteConfig(this._data.noteConfig)
  }

  get reportConfig(): WorkflowReportConfig {
    return this._data.reportConfig ?? {}
  }

  get aiConfig(): WorkflowStageAiConfig {
    return this._data.aiConfig ?? {}
  }
}

export class WorkflowAction {
  private readonly _data: WorkflowActionData
  private readonly _workflow: Workflow

  constructor(data: WorkflowActionData, workflow: Workflow) {
    this._workflow = workflow
    this._data = data
  }

  get data(): WorkflowActionData {
    return this._data
  }

  get id(): WorkflowActionId {
    return this._data.actionId
  }

  get name(): string {
    return this._data.name
  }

  get short(): string {
    return getActionTileLabel(this._data)
  }

  get plural(): string {
    const { name, plural } = this._data
    return plural || name
  }

  get description(): string | undefined {
    if (isNotEmpty(this._data.description)) return this._data.description
    if (isDefined(this.toStage)) return this.toStage.description
  }

  get icon(): string {
    return this._data.icon
  }

  get color(): string | undefined {
    if (isValidHexColor(this._data.color)) return this._data.color
  }

  get fromStages(): WorkflowStage[] {
    return this._workflow.stages.filter((s) => s.actionIds.includes(this.id)).filter(isNotDeleted)
  }

  get toStage(): WorkflowStage | undefined {
    if (isNotDefined(this._data.toStageId)) return
    return this._workflow.getStage(this._data.toStageId)
  }

  public getDisplayProps(
    account?: Pick<AccountData, 'workflowStageId' | 'workflowHistory'>
  ): Extract<PinDisplayProps, { type: 'icon' | 'logo' }> {
    const targetStage = this.getDisplayTargetStage(account)
    if (isNotDefined(targetStage)) return { type: 'icon', color: this.color ?? PROSPECT_STAGE_COLOR, icon: this.icon }
    if (targetStage.useCompanyLogo) return { type: 'logo', color: targetStage.color }
    return { type: 'icon', color: this.color ?? targetStage.color, icon: this.icon }
  }

  get isDeleted(): boolean {
    if (isDeleted(this._data)) return true
    if (isDefined(this.toStage) && isDeleted(this.toStage)) return true
    return false
  }

  private getEventConfig(currentStageId?: WorkflowStageId): WorkflowEventConfig | undefined {
    const targetStage = this.toStage ?? this._workflow.getStage(currentStageId)
    const stageConfig = targetStage?.eventConfig
    if (isTrue(stageConfig?.scheduleAppointment)) return stageConfig
    const actionConfig = this._data.accountConfig
    if (isTrue(actionConfig?.scheduleAppointment)) return actionConfig
    return undefined
  }

  public getScheduleAppointmentType(
    currentStageId: WorkflowStageId | undefined,
    closerModel: CloserModel | undefined,
    closerInteraction: CloserInteraction | undefined
  ): 'personal' | 'closer' | 'both' | undefined {
    const eventConfig = this.getEventConfig(currentStageId)
    if (isNotDefined(eventConfig)) return
    if (!isTrue(eventConfig.scheduleAppointment)) return
    const canHaveCloserEvent = isDefined(closerModel) && closerInteraction !== 'None'
    if (!canHaveCloserEvent) return 'personal'
    const appointmentType = eventConfig.appointmentType
    if (isNotDefined(appointmentType)) return 'both'
    return appointmentType
  }

  public getEffectiveScheduleRestriction(currentStageId?: WorkflowStageId): WorkflowScheduleRestriction | undefined {
    const eventConfig = this.getEventConfig(currentStageId)
    return eventConfig?.scheduleRestriction ?? this._workflow.defaultScheduleRestriction
  }

  private get hasLocationBasedConditions(): boolean {
    return Boolean(this._data.conditions?.some(isLocationBasedCondition))
  }

  get trackUserLocation(): boolean {
    if (isNotDefined(this._data.accountConfig?.trackUserLocation)) return true
    return this._data.accountConfig.trackUserLocation
  }

  get reportConfig(): WorkflowReportConfig {
    const { hoursWorked, ...config } = this._data.reportConfig ?? {}
    return {
      ...config,
      hoursWorked: isDefined(hoursWorked) ? hoursWorked : { enabled: true },
    }
  }

  get shouldRequireUserLocation(): boolean {
    if (this.trackUserLocation) return true
    if (this.hasLocationBasedConditions) return true
    return false
  }

  private getDisplayTargetStage(
    account?: Pick<AccountData, 'workflowStageId' | 'workflowHistory'>
  ): WorkflowStage | undefined {
    if (this.toStage) return this.toStage
    if (isNotEmpty(this.checkpoints)) {
      const currentStage = this._workflow.getStage(account?.workflowStageId)
      const lastAlwaysCheckpoint = this._workflow
        .getActionCheckpoints(account, this.id)
        ?.findLast((c) => c.behavior === 'always')
      if (isDefined(lastAlwaysCheckpoint)) {
        if (isNotDefined(currentStage) || currentStage.order < lastAlwaysCheckpoint.stage.order)
          return lastAlwaysCheckpoint.stage
      }
    }
    return this._workflow.getStage(account?.workflowStageId ?? WORKFLOW_PROSPECT_STAGE_ID)
  }

  public hasPermission(userPerm: UserPerm): boolean {
    const requiredRoles = this._data.roles
    if (isEmpty(requiredRoles)) return true
    return Boolean(userPerm.roles?.some((role) => requiredRoles.some((r) => r === role.roleId)))
  }

  get checkpoints(): ActionStageCheckpoint[] {
    if (isDefined(this.toStage)) {
      if (this.toStage.isPipelineStage || this.toStage.isDeleted) return []
    }
    if (isEmpty(this.data.checkpoints)) return []

    return entries(this.data.checkpoints)
      .map(([stageId, config]) => {
        if (isNotDefined(config)) return
        const stage = this.fromStages.find((s) => s.id === stageId)
        if (isNotDefined(stage)) return
        if (isNotDefined(config.behavior)) return
        return {
          stage,
          behavior: config.behavior,
        }
      })
      .filter(isDefined)
      .filter((a) => a.stage.isPipelineStage && !a.stage.isDeleted && !a.stage.isInitial)
      .sort((a, b) => a.stage.order - b.stage.order)
  }

  private get noteConfig(): Required<WorkflowNoteConfig> | undefined {
    return decorateNoteConfig(this._data.accountConfig?.noteConfig)
  }

  public getNoteConfig(currentStageId?: WorkflowStageId): Required<WorkflowNoteConfig> | undefined {
    if (isDefined(this.noteConfig)) return this.noteConfig

    const targetStageConfig = this.toStage?.noteConfig
    if (isDefined(targetStageConfig)) return targetStageConfig

    if (isDefined(currentStageId)) {
      const currentStage = this._workflow.getStage(currentStageId)
      if (isDefined(currentStage)) return currentStage.noteConfig
    }

    return undefined
  }

  public shouldRequireNote(
    stageId: WorkflowStageId | undefined,
    account: Pick<AccountData, 'notes'> | undefined,
    currentUserId: UserId
  ): boolean {
    const noteConfig = this.getNoteConfig(stageId)
    if (isNotDefined(noteConfig)) return false
    if (!isTrue(noteConfig?.required)) return false
    if (isNotEmpty(account?.notes)) {
      const now = Date.now()
      const { timeframeMs } = noteConfig
      const hasRecentNote = account.notes.some((note) => {
        if (note.userId !== currentUserId) return false
        const diff = now - note.timestamp
        return diff <= timeframeMs
      })
      if (hasRecentNote) return false
    }
    return true
  }
}

const decorateNoteConfig = (config: WorkflowNoteConfig | undefined): Required<WorkflowNoteConfig> | undefined => {
  if (isNotDefined(config)) return
  return {
    ...config,
    timeframeMs: config.timeframeMs ?? DEFAULT_NOTE_TIMEFRAME_MS,
    required: isTrue(config.required),
  }
}

export type ActionStageCheckpoint = {
  stage: WorkflowStage
  behavior: CheckpointBehavior
}

type UserPerm = Pick<UserProfileData, 'permissionMap' | 'roles'>

export type PinDisplayProps =
  | { type: 'initial' }
  | {
      type: 'logo'
      color: string
    }
  | {
      type: 'tag'
      tagId: TagId
    }
  | {
      type: 'icon'
      color: string
      icon?: string
    }

const isLocationBasedCondition = (condition: WorkflowCondition): boolean => {
  return ['distance_from_action', 'distance_from_property'].includes(condition.type)
}

export const sortWorkflowActions = (actions: WorkflowAction[]): WorkflowAction[] => {
  return actions.sort((a, b) => {
    if (isDeleted(a)) return 1
    if (isDeleted(b)) return -1
    const aTarget = a.toStage
    const bTarget = b.toStage
    if (!aTarget && !bTarget) return a.name.localeCompare(b.name)
    if (!aTarget) return 1
    if (!bTarget) return -1
    const aPipeline = aTarget.isPipelineStage
    const bPipeline = bTarget.isPipelineStage
    if (aPipeline && bPipeline) {
      const orderCmp = aTarget.order - bTarget.order
      if (orderCmp !== 0) return orderCmp
      return a.name.localeCompare(b.name)
    }
    if (aPipeline) return -1
    if (bPipeline) return 1
    const stageNameCmp = aTarget.name.localeCompare(bTarget.name)
    if (stageNameCmp !== 0) return stageNameCmp
    return a.name.localeCompare(b.name)
  })
}
